import { Fragment, h } from "preact";
import { memo } from "react";
import { compareRender } from "../../../functions/form";
import { useController, UseControllerProps } from "react-hook-form";
import { Input as InputAntd, Row } from "antd";
import { Text } from "../../text";
import { ComponentInputSearch, ContainerNoText } from "./styled";
import { ColComponent, Required, RowComponent } from "../../../style/styled";

const { TextArea } = InputAntd;

// readOnly = แสดงอย่างเดียว แก้ไม่ได้ แต่อ่านง่ายกว่า disabled (พื้นเทาอ่อน ตัวอักษรเข้ม)
const readOnlyStyle = {
  backgroundColor: "#f5f5f5",
  color: "rgba(0,0,0,0.85)",
  cursor: "default",
};

interface IItem extends UseControllerProps {
  ref: string;
  id: string;
  format?: string;
  label?: string;
  placeholder?: string;
  span?: number;
  disabled?: boolean;
  readOnly?: boolean;
  onEnter?(i: string, force: boolean): Promise<void>;
  loading?: boolean;
}

interface IInput {
  control?: any;
  item: IItem;
  handleChange?: any;
}

interface IInputLabel {
  item: IItem;
  handleChange?: any;
  value?: string;
  onEnter?(i: string, force: boolean): Promise<void>;
  hasMargin?: boolean;
}

function handleKeyDown(
  event: React.KeyboardEvent<HTMLInputElement>,
  onEnter?: any
) {
  if (event.key === "Enter") {
    const { value: v } = event.target as HTMLInputElement;
    onEnter && onEnter(v, true);
  }
}

const Input = ({ control, item, handleChange }: IInput) => {
  const {
    ref,
    rules,
    name,
    defaultValue,
    label,
    placeholder,
    disabled,
    readOnly,
    onEnter,
  } = item;

  const { field, fieldState } = useController({
    control,
    name,
    rules,
    defaultValue,
  });
  const { error } = fieldState;
  const { value } = field;

  return (
    <>
      <RowComponent>
        {label && <Text size={"XS"} text={label} bold={true} />}
        {rules && rules.required && <Required>*</Required>}
      </RowComponent>
      <InputAntd
        id={ref}
        name={ref}
        value={value}
        disabled={disabled}
        readOnly={readOnly}
        style={readOnly ? readOnlyStyle : undefined}
        onChange={(e) => handleChange(e, item)}
        onKeyDown={(e) => handleKeyDown(e, onEnter)}
        placeholder={placeholder}
        autoComplete={"off"}
      />
      <Row>
        {error &&
          ((
            <Text size={"XS"} text={error.message} color="red" bold />
          ) as React.ReactNode)}
      </Row>
    </>
  );
};

const InputSearch = ({ control, item, handleChange }: IInput) => {
  const {
    ref,
    rules,
    name,
    defaultValue,
    label,
    placeholder,
    disabled,
    onEnter,
    loading,
  } = item;
  const { field, fieldState } = useController({
    control,
    name,
    rules,
    defaultValue,
  });
  const { error } = fieldState;
  const { value } = field;

  return (
    <>
      <Row>
        {
          (label !== "" || label ? (
            <Text size={"XS"} text={label} bold />
          ) : (
            <ContainerNoText />
          )) as React.ReactNode
        }
      </Row>
      <ComponentInputSearch
        id={ref}
        name={ref}
        value={value}
        disabled={disabled}
        onChange={(e) => handleChange(e, item)}
        onKeyDown={(e) => handleKeyDown(e, onEnter)}
        placeholder={placeholder}
        autoComplete={"off"}
        loading={loading}
      />
      <Row>
        {error &&
          ((
            <Text size={"XS"} text={error.message} color="red" bold />
          ) as React.ReactNode)}
      </Row>
    </>
  );
};

export const InputLabel = ({
  item,
  value,
  handleChange = () => null,
  onEnter,
  hasMargin,
}: IInputLabel) => {
  const { ref, label, placeholder, span } = item;

  return (
    <ColComponent
      xs={{ span: 24 }}
      md={{ span: 24 }}
      xl={{ span }}
      lg={{ span }}
    >
      <Row style={{ marginLeft: hasMargin ? "10px" : "" }}>
        {label && ((<Text size={"XS"} text={label} bold />) as React.ReactNode)}
      </Row>
      <InputAntd
        id={ref}
        name={ref}
        value={value}
        onChange={(e) => handleChange(e, item)}
        placeholder={placeholder}
        autoComplete={"off"}
        onKeyDown={(e) => handleKeyDown(e, onEnter)}
        style={{ fontWeight: 600, marginLeft: hasMargin ? "10px" : "" }}
      />
    </ColComponent>
  );
};

const TextAreaAntd = ({ control, item, handleChange }: IInput) => {
  const { ref, rules, name, defaultValue, label, placeholder, disabled, readOnly } =
    item;
  const { field, fieldState } = useController({
    control,
    name,
    rules,
    defaultValue,
  });
  const { error } = fieldState;
  const { value } = field;

  return (
    <>
      <RowComponent>
        {label &&
          ((<Text size={"XS"} text={label} bold={true} />) as React.ReactNode)}
        {rules && rules.required && <Required>*</Required>}
      </RowComponent>
      <TextArea
        autoSize
        id={ref}
        name={ref}
        value={value}
        onChange={(e) => handleChange(e, item)}
        placeholder={placeholder}
        autoComplete={"off"}
        disabled={disabled}
        readOnly={readOnly}
        style={readOnly ? readOnlyStyle : undefined}
      />
      <Row>
        {error &&
          ((
            <Text size={"XS"} text={error.message} color="red" bold />
          ) as React.ReactNode)}
      </Row>
    </>
  );
};

export const MemoizedInput = memo(
  Input,
  (prev, next) =>
    compareRender(prev, next) &&
    JSON.parse(JSON.stringify(prev.item)) ===
      JSON.parse(JSON.stringify(next.item))
);
export const MemoizedTextAreaAntd = memo(
  TextAreaAntd,
  (prev, next) =>
    compareRender(prev, next) && compareRender(prev, next, "item.disabled")
);
export const MemoizedInputLabel = memo(InputLabel, compareRender);
export const MemoizedInputSeach = memo(
  InputSearch,
  (prev, next) =>
    compareRender(prev, next) &&
    JSON.parse(JSON.stringify(prev.item.loading)) ===
      JSON.parse(JSON.stringify(next.item.loading))
);
